import { memo, useState } from "react";
import { Box, Divider, Alert } from "@mui/material";
import { AlertCircle } from "lucide-react";
import AuditInfo from "../onboarding/details/AuditInfo";
import DocumentsSection from "../onboarding/details/DocumentsSection";
import FileViewDialog from "../onboarding/details/FileViewDialog";
import EMIBreakdown from "../onboarding/details/EMIBreakdown";
import LoanSummaryTable from "../onboarding/details/LoanSummaryTable";
import DetailsSection from "../onboarding/details/DetailsSection";

const LeadDetails = memo(({ data }) => {
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    file: null,
    fileName: "",
  });

  if (!data) {
    return (
      <Alert severity="info" icon={<AlertCircle size={20} />}>
        No pre-onboarding details available
      </Alert>
    );
  }

  const handlePreview = (file, fileName) => {
    setPreviewDialog({
      open: true,
      file,
      fileName,
    });
  };

  const handleClosePreview = () => {
    setPreviewDialog({
      open: false,
      file: null,
      fileName: "",
    });
  };

  const driverDetails = {
    driverName: data.driverName || "",
    driverId: data.driverId || "",
    phoneNumber: data.phoneNumber || "",
    age: data.age ? `${data.age} years` : "",
    address: data.address || "",
    city: data.city || "",
    location: data.location || "",
    eVehicleNumber: data.eVehicleNumber || "",
  };

  const assetsDetails = {
    batteryImei: data?.assetAllocation?.batteryImei || "",
    batteryType: data?.assetAllocation?.batteryType || "",
    chargerId: data?.assetAllocation?.chargerId || "",
    ...(data?.assetAllocation || {}),
  };

  const vehicleFinancierDetails = data.vehicleFinancierDetails || {};

  const isVehicleFinanced =
    data.vehicleFinanced === "Yes" && vehicleFinancierDetails.name;

  const loanSummary = data.loanSummary || {};
  const documentUpload = data.documentUpload || {};

  const documents = Object.fromEntries(
    [
      ["aadhaarFront", documentUpload.aadhaarFrontUrl, "Aadhaar Front"],
      ["aadhaarBack", documentUpload.aadhaarBackUrl, "Aadhaar Back"],
      ["panCard", documentUpload.panCardUrl, "PAN Card"],
      ["crifReport", documentUpload.crifReportUrl, "Crif Report"],
      ["rcFront", documentUpload.rcFrontUrl, "RC Front"],
      ["rcBack", documentUpload.rcBackUrl, "RC Back"],
    ]
      .filter(([, url]) => url)
      .map(([key, url, name]) => [key, { url, name }]),
  );

  return (
    <Box sx={{ py: 2 }}>
      <DetailsSection
        title="Lead Driver Details"
        data={{ ...driverDetails, vehicleFinanced: data.vehicleFinanced }}
      />

      <DetailsSection title="Assets" data={assetsDetails} />

      {isVehicleFinanced && (
        <DetailsSection
          title="Vehicle Financier Details"
          data={{
            vehicleFinancierName: vehicleFinancierDetails.name,
            phoneNumber: vehicleFinancierDetails.phoneNumber,
            address: vehicleFinancierDetails.address,
          }}
        />
      )}

      {Object.keys(loanSummary).length > 0 && (
        <LoanSummaryTable
          loanData={{
            financier:
              loanSummary.financierDisplayName || loanSummary.financier,
            cibilScore: loanSummary.cibilScore || documentUpload.cibilNumber,
            interestRate: loanSummary.interestRate,
            downPayment: loanSummary.downPayment,
            invoiceAmount: loanSummary.invoiceAmount,
            loanAmount: loanSummary.loanAmount,
            tenure: loanSummary.tenure,
          }}
        />
      )}

      {loanSummary.emi && <EMIBreakdown emiData={loanSummary.emi} />}

      <Divider sx={{ my: 3 }} />

      <DocumentsSection documents={documents} onPreview={handlePreview} />

      <FileViewDialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        file={previewDialog.file}
        fileName={previewDialog.fileName}
      />

      <AuditInfo data={data} variant="card" />
    </Box>
  );
});

export default LeadDetails;
